import { useCallback, useEffect, useMemo, useState } from 'react'
import ApiService from '@services/api'
import { useNotification } from '@context/NotificationContext'
import Modal from '@components/shared/Modal'

const LEGAL_AREA_OPTIONS = [
  { value: 'CIVEL', label: 'Cível' },
  { value: 'TRABALHISTA', label: 'Trabalhista' },
  { value: 'TRIBUTARIO', label: 'Tributário' },
  { value: 'JUIZADO', label: 'Juizado Especial' },
]

function normalizeLegalArea(value) {
  if (!value) return ''
  const normalized = String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase().replace(/[\s-]+/g, '_')
  const aliases = { CIVIL: 'CIVEL', JUIZADO_ESPECIAL: 'JUIZADO', JUIZADOS_ESPECIAIS: 'JUIZADO' }
  return aliases[normalized] || normalized
}

function getField(source, camelKey, pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1), fallback = undefined) {
  return source?.[camelKey] ?? source?.[pascalKey] ?? fallback
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return '0%'
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue)) return String(value)
  return `${Math.round(numericValue * 100)}%`
}

function formatDateTime(value) {
  if (!value) return 'Não informado'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('pt-BR')
}

function formatDateOnly(value) {
  if (!value) return 'Não informado'
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const date = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString('pt-BR')
}

function responseList(response) {
  return response.success && Array.isArray(response.data) ? response.data : []
}

function firstFailedResponse(responses) {
  return responses.find((response) => !response.success)
}

function MetricCard({ label, value, helper, tone = 'is-info' }) {
  return (
    <div className={`deadline-learning-metric ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {helper ? <small>{helper}</small> : null}
    </div>
  )
}

function CandidateBadge({ candidate }) {
  const reviewed = getField(candidate, 'reviewed')
  const trusted = getField(candidate, 'trusted')

  if (!reviewed) return <span className="status-badge is-warning">Pendente</span>
  return <span className={`status-badge ${trusted ? 'is-success' : 'is-muted'}`}>{trusted ? 'Confiável' : 'Rejeitado'}</span>
}

function ExampleBadge({ example }) {
  const status = getField(example, 'validationStatus', 'ValidationStatus', 'PENDING')
  if (status === 'CORRECT') return <span className="status-badge is-success">Correto</span>
  if (status === 'INCORRECT') return <span className="status-badge is-muted">Corrigido</span>
  return <span className="status-badge is-warning">Pendente</span>
}

function ExampleReviewModal({ open, example, actions, saving, onClose, onSubmit }) {
  const [correct, setCorrect] = useState(true)
  const [actionCode, setActionCode] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [reason, setReason] = useState('')
  const [area, setArea] = useState('')
  const [regime, setRegime] = useState('')
  const [microsystem, setMicrosystem] = useState('')
  const [documentNature, setDocumentNature] = useState('')

  useEffect(() => {
    if (!example) return
    setCorrect(getField(example, 'validationStatus', 'ValidationStatus', 'PENDING') !== 'INCORRECT')
    setActionCode(getField(example, 'finalActionCode', 'FinalActionCode', ''))
    setExcerpt(getField(example, 'selectedExcerpt', 'SelectedExcerpt') || getField(example, 'judgeIntentFragment', 'JudgeIntentFragment', ''))
    setReason('')
    setArea(normalizeLegalArea(getField(example, 'validatedArea', 'ValidatedArea') || getField(example, 'detectedArea', 'DetectedArea', '')))
    setRegime(getField(example, 'validatedRegime', 'ValidatedRegime') || getField(example, 'detectedRegime', 'DetectedRegime', ''))
    setMicrosystem(getField(example, 'validatedMicrosystem', 'ValidatedMicrosystem') || getField(example, 'detectedMicrosystem', 'DetectedMicrosystem', ''))
    setDocumentNature(getField(example, 'validatedDocumentNature', 'ValidatedDocumentNature') || getField(example, 'documentNature', 'DocumentNature', ''))
  }, [example])

  if (!example) return null
  const originalText = getField(example, 'originalText', 'OriginalText', '')

  return (
    <Modal isOpen={open} onClose={onClose} title="Validar exemplo de aprendizado" subtitle="Confira a publicação integral e defina o trecho que realmente identifica a ação." size="large">
      <div className="deadline-learning-modal">
        <div className="deadline-learning-modal-context">
          <strong>{getField(example, 'processNumber', 'ProcessNumber', 'Processo não informado')}</strong>
          <span>Confirmed Action Code: {getField(example, 'finalActionCode', 'FinalActionCode', '-')}</span>
        </div>
        <label className="form-group">
          <span>Publicação completa</span>
          <textarea value={originalText} rows={10} readOnly />
        </label>
        <div className="deadline-learning-context-grid">
          <label className="form-group">
            <span>Área *</span>
            <select value={area} onChange={(event) => setArea(event.target.value)}>
              <option value="">Selecione uma área...</option>
              {LEGAL_AREA_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <label className="form-group"><span>Natureza do documento *</span><input value={documentNature} onChange={(event) => setDocumentNature(event.target.value)} /></label>
          <label className="form-group"><span>Regime</span><input value={regime} onChange={(event) => setRegime(event.target.value)} /></label>
          <label className="form-group"><span>Microssistema</span><input value={microsystem} onChange={(event) => setMicrosystem(event.target.value)} /></label>
        </div>
        <fieldset className="deadline-feedback-choice-group">
          <legend>A ação sugerida está correta?</legend>
          <label><input type="radio" name="exampleCorrect" checked={correct} onChange={() => setCorrect(true)} /> Sim</label>
          <label><input type="radio" name="exampleCorrect" checked={!correct} onChange={() => setCorrect(false)} /> Não, corrigir</label>
        </fieldset>
        {!correct ? (
          <label className="form-group">
            <span>Ação correta</span>
            <select value={actionCode} onChange={(event) => setActionCode(event.target.value)}>
              <option value="">Selecione...</option>
              {actions.map((action) => <option key={getField(action, 'id', 'Id')} value={getField(action, 'code', 'Code')}>{getField(action, 'code', 'Code')} — {getField(action, 'name', 'Name')}</option>)}
            </select>
          </label>
        ) : null}
        <label className="form-group">
          <span>Trecho seletor</span>
          <textarea value={excerpt} rows={4} onChange={(event) => setExcerpt(event.target.value)} placeholder="Copie ou ajuste um trecho existente na publicação." />
        </label>
        <label className="form-group">
          <span>Observação (opcional)</span>
          <textarea value={reason} rows={2} onChange={(event) => setReason(event.target.value)} />
        </label>
        {(getField(example, 'validationHistory', 'ValidationHistory', []) || []).length > 0 ? (
          <div className="deadline-learning-history">
            <strong>Histórico de validações</strong>
            {(getField(example, 'validationHistory', 'ValidationHistory', []) || []).map((revision) => (
              <div key={getField(revision, 'id', 'Id')}>
                <span>Versão {getField(revision, 'revision', 'Revision')} · {getField(revision, 'actionCode', 'ActionCode')}</span>
                <small>{getField(revision, 'reviewedByUserId', 'ReviewedByUserId')} · {getField(revision, 'reason', 'Reason', 'Sem observação')}</small>
              </div>
            ))}
          </div>
        ) : null}
        <div className="deadline-learning-modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="button" className="btn btn-primary" disabled={saving || !excerpt.trim() || !area.trim() || !documentNature.trim() || (!correct && !actionCode)} onClick={() => onSubmit({ correct, actionCode: correct ? null : actionCode, selectedExcerpt: excerpt.trim(), reason: reason.trim() || null, area: area.trim(), regime: regime.trim() || null, microsystem: microsystem.trim() || null, documentNature: documentNature.trim(), expectedVersion: getField(example, 'validationVersion', 'ValidationVersion', 0) })}>
            <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`} /> {saving ? 'Salvando...' : 'Confirmar validação'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function CandidateReviewModal({ open, candidate, approved, saving, onClose, onChangeApproved, onSubmit }) {
  const actionName = getField(candidate, 'actionCode', 'ActionCode', 'ação')
  const phrase = getField(candidate, 'phrase', 'Phrase', '')

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Revisar candidato"
      subtitle="Confirme se a frase deve ser promovida como padrão confiável do motor."
      size="medium"
    >
      <div className="deadline-learning-modal">
        <div className="deadline-learning-modal-context">
          <strong>{actionName}</strong>
          <span>{phrase || 'Sem frase cadastrada.'}</span>
        </div>

        <fieldset className="deadline-feedback-choice-group">
          <legend>Esse candidato deve ser confiável?</legend>
          <label>
            <input type="radio" name="candidateReview" value="true" checked={approved === true} onChange={() => onChangeApproved(true)} />
            Sim, confiar no padrão
          </label>
          <label>
            <input type="radio" name="candidateReview" value="false" checked={approved === false} onChange={() => onChangeApproved(false)} />
            Não, manter rejeitado
          </label>
        </fieldset>

        <div className="deadline-learning-modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving}>
            <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`} />
            {saving ? 'Salvando...' : 'Confirmar revisão'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function PanelSection({ title, description, action, children }) {
  return (
    <section className="rules-card deadline-settings-card">
      <div className="rules-card-header">
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        {action ? <div className="rules-card-actions">{action}</div> : null}
      </div>
      <div className="rules-card-body">{children}</div>
    </section>
  )
}

function Visible({ when, children }) {
  return when ? children : null
}

function hasItems(loading, items) {
  return !loading && items.length > 0
}

function hasNoItems(loading, items) {
  return !loading && items.length === 0
}

function BusyIcon({ busy, idleIcon }) {
  return <i className={`fas ${busy ? 'fa-spinner fa-spin' : idleIcon}`} />
}

function LastImportMetric({ lastImportedAt }) {
  const value = lastImportedAt ? 'Concluída' : 'Nenhuma'
  const helper = lastImportedAt ? formatDateTime(lastImportedAt) : 'Aguardando a primeira importação'
  return <MetricCard label="Última importação" value={value} helper={helper} />
}

function displayField(source, camelKey, pascalKey) {
  return getField(source, camelKey, pascalKey) || '-'
}

function exampleFinalAction(example) {
  return getField(example, 'finalActionCode', 'FinalActionCode') || getField(example, 'engineActionCode', 'EngineActionCode') || '-'
}

function LearningExampleRow({ example, onReview }) {
  const exampleId = getField(example, 'id', 'Id')
  const correctionReason = getField(example, 'correctionReason', 'CorrectionReason')
  const validationStatus = getField(example, 'validationStatus', 'ValidationStatus', 'PENDING')

  return (
    <tr key={exampleId}>
      <td>{displayField(example, 'source', 'Source')}</td>
      <td>{displayField(example, 'processNumber', 'ProcessNumber')}</td>
      <td>{displayField(example, 'publicationId', 'PublicationId')}</td>
      <td>
        <div className="deadline-learning-phrase">
          {displayField(example, 'humanInterpretation', 'HumanInterpretation')}
          {correctionReason ? <small>{correctionReason}</small> : null}
        </div>
      </td>
      <td>{exampleFinalAction(example)}</td>
      <td>{formatDateOnly(getField(example, 'finalDueDate', 'FinalDueDate'))}</td>
      <td>{formatPercent(getField(example, 'confidence', 'Confidence'))}</td>
      <td><ExampleBadge example={example} /></td>
      <td>{formatDateTime(getField(example, 'createdAtUtc', 'CreatedAtUtc'))}</td>
      <td><button type="button" className="btn btn-sm btn-outline" onClick={() => onReview(example)} disabled={validationStatus !== 'PENDING'}><i className="fas fa-check" /> Validar</button></td>
    </tr>
  )
}

export default function DeadlineEngineLearningPanel() {
  const notify = useNotification()
  const [examples, setExamples] = useState([])
  const [totalExamples, setTotalExamples] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(25)
  const [actionFilter, setActionFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('PENDING')
  const [searchFilter, setSearchFilter] = useState('')
  const [actions, setActions] = useState([])
  const [stats, setStats] = useState([])
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [savingId, setSavingId] = useState(null)
  const [reviewModalOpen, setReviewModalOpen] = useState(false)
  const [reviewCandidate, setReviewCandidate] = useState(null)
  const [reviewApproved, setReviewApproved] = useState(true)
  const [lastLoadedAt, setLastLoadedAt] = useState(null)
  const [lastImportedAt, setLastImportedAt] = useState(null)
  const [exampleModalOpen, setExampleModalOpen] = useState(false)
  const [reviewExample, setReviewExample] = useState(null)
  const [savingExample, setSavingExample] = useState(false)

  const loadLearningState = useCallback(async () => {
    setLoading(true)
    const [examplesResponse, statsResponse, candidatesResponse, configurationsResponse] = await Promise.all([
      ApiService.getDeadlineAdminLearningExamples({ finalActionCode: actionFilter, validationStatus: statusFilter, search: searchFilter, page, pageSize }),
      ApiService.getDeadlineLearningStats(),
      ApiService.getDeadlinePatternCandidates(),
      ApiService.getDeadlineEngineConfigurations(),
    ])
    setLoading(false)

    const failedResponse = firstFailedResponse([examplesResponse, statsResponse, candidatesResponse, configurationsResponse])
    if (failedResponse) notify.showError(failedResponse.error || 'Não foi possível carregar os dados de aprendizado.')

    const examplePage = examplesResponse.data || {}
    setExamples(getField(examplePage, 'items', 'Items', []))
    setTotalExamples(Number(getField(examplePage, 'total', 'Total', 0) || 0))
    setStats(responseList(statsResponse))
    setCandidates(responseList(candidatesResponse))
    const configurations = configurationsResponse.data || {}
    setActions(getField(configurations, 'canonical_actions', 'canonicalActions', getField(configurations, 'CanonicalActions', 'CanonicalActions', [])) || [])
    setLastLoadedAt(new Date().toISOString())
  }, [actionFilter, notify, page, pageSize, searchFilter, statusFilter])

  useEffect(() => {
    loadLearningState()
  }, [loadLearningState])

  const summary = useMemo(() => {
    const visibleExamples = examples.length
    const reviewedExamples = examples.filter((example) => getField(example, 'reviewed') === true).length
    const trustedExamples = examples.filter((example) => getField(example, 'trusted') === true).length
    const pendingExamples = examples.filter((example) => getField(example, 'validationStatus', 'ValidationStatus') === 'PENDING').length
    const totalCandidates = candidates.length
    const totalMatches = stats.reduce((acc, stat) => acc + Number(getField(stat, 'totalMatches', 'TotalMatches', 0) || 0), 0)
    const latestSource = examples[0] ? getField(examples[0], 'source', 'Source', 'HISTORICAL_IMPORT') : 'HISTORICAL_IMPORT'

    return {
      totalExamples,
      visibleExamples,
      reviewedExamples,
      trustedExamples,
      pendingExamples,
      totalCandidates,
      totalMatches,
      latestSource,
    }
  }, [candidates.length, examples, stats, totalExamples])

  const latestExamples = examples
  const topStats = useMemo(() => stats.slice(0, 8), [stats])

  const candidateIdOf = (candidate) => getField(candidate, 'candidateId', 'CandidateId')

  const openReviewModal = (candidate) => {
    setReviewCandidate(candidate)
    setReviewApproved(Boolean(getField(candidate, 'trusted')))
    setReviewModalOpen(true)
  }

  const closeReviewModal = () => {
    setReviewModalOpen(false)
    setReviewCandidate(null)
  }

  const submitReview = async () => {
    if (!reviewCandidate) return

    const candidateId = candidateIdOf(reviewCandidate)
    setSavingId(candidateId)
    const response = await ApiService.reviewDeadlinePatternCandidate(candidateId, {
      approved: reviewApproved,
    })
    setSavingId(null)

    if (!response.success) {
      notify.showError(response.error || 'Não foi possível revisar o candidato.')
      return
    }

    notify.showSuccess(reviewApproved ? 'Candidato aprovado com sucesso.' : 'Candidato rejeitado com sucesso.')
    closeReviewModal()
    await loadLearningState()
  }

  const quickReview = async (candidate, approved) => {
    const id = candidateIdOf(candidate)
    setSavingId(id)
    const response = approved
      ? await ApiService.approveDeadlinePatternCandidate(id)
      : await ApiService.rejectDeadlinePatternCandidate(id)
    setSavingId(null)

    if (!response.success) {
      notify.showError(response.error || 'Não foi possível atualizar o candidato.')
      return
    }

    notify.showSuccess(approved ? 'Candidato aprovado com sucesso.' : 'Candidato rejeitado com sucesso.')
    await loadLearningState()
  }

  const importPublicHistory = async () => {
    setImporting(true)
    const response = await ApiService.importPublicHistoricalLearning()
    setImporting(false)

    if (!response.success) {
      notify.showError(response.error || 'Não foi possível importar o histórico público.')
      return
    }

    const data = response.data || {}
    const importedCount = getField(data, 'importedCount', 'ImportedCount', 0)
    const pendingReviewCount = getField(data, 'pendingReviewCount', 'PendingReviewCount', 0)
    const candidatePatternCount = getField(data, 'candidatePatternCount', 'CandidatePatternCount', 0)
    const responseCandidates = Array.isArray(getField(data, 'candidates', 'Candidates')) ? getField(data, 'candidates', 'Candidates') : []

    notify.showSuccess(
      `Importação concluída: ${importedCount} novos exemplos, ${pendingReviewCount} pendentes de revisão e ${candidatePatternCount} candidatos gerados.`,
    )
    setLastImportedAt(new Date().toISOString())
    if (responseCandidates.length > 0) {
      setCandidates(responseCandidates)
    }
    await loadLearningState()
  }

  const openExampleReview = async (example) => {
    const id = getField(example, 'id', 'Id')
    setSavingId(id)
    const response = await ApiService.getDeadlineAdminLearningExample(id)
    setSavingId(null)
    if (!response.success) {
      notify.showError(response.error || 'Não foi possível carregar a publicação completa.')
      return
    }
    setReviewExample(response.data)
    setExampleModalOpen(true)
  }

  const closeExampleReview = () => {
    if (savingExample) return
    setExampleModalOpen(false)
    setReviewExample(null)
  }

  const validateExample = async (payload) => {
    const id = getField(reviewExample, 'id', 'Id')
    setSavingExample(true)
    const isCorrection = getField(reviewExample, 'validationVersion', 'ValidationVersion', 0) > 0
    const response = isCorrection
      ? await ApiService.correctDeadlineAdminLearningExample(id, payload)
      : await ApiService.validateDeadlineAdminLearningExample(id, payload)
    setSavingExample(false)
    if (!response.success) {
      notify.showError(response.error || 'Não foi possível validar o exemplo.')
      return
    }
    notify.showSuccess(payload.correct ? 'Ação confirmada em aprendizado supervisionado.' : 'Validação corrigida e aprendizado recalculado.')
    setExampleModalOpen(false)
    setReviewExample(null)
    await loadLearningState()
  }

  return (
    <div className="deadline-learning-panel">
      <div className="deadline-learning-overview">
        <div>
          <span className="deadline-learning-eyebrow">Aprendizado do motor</span>
          <h4>Histórico público, exemplos analisados e curadoria dos candidatos</h4>
          <p>Importe a base derivada do vínculo entre prazos e publicações, acompanhe os exemplos aprendidos e revise os padrões gerados antes de promover o conhecimento.</p>
        </div>
        <div className="deadline-learning-overview-actions">
          <button type="button" className="btn btn-primary" onClick={importPublicHistory} disabled={loading || importing}>
            <BusyIcon busy={importing} idleIcon="fa-database" />
            {importing ? 'Importando...' : 'Importar histórico do public'}
          </button>
          <button type="button" className="btn btn-outline" onClick={loadLearningState} disabled={loading}>
            <BusyIcon busy={loading} idleIcon="fa-rotate-right" />
            {loading ? 'Atualizando...' : 'Atualizar painel'}
          </button>
        </div>
      </div>

      <div className="deadline-learning-metrics">
        <MetricCard label="Exemplos" value={summary.totalExamples} helper="Exemplos de aprendizado persistidos" />
        <MetricCard label="Pendentes" value={summary.pendingExamples} helper="Ainda aguardando revisão" tone="is-warning" />
        <MetricCard label="Revisados" value={summary.reviewedExamples} helper="Marcados como confiáveis ou rejeitados" tone="is-success" />
        <MetricCard label="Confiáveis" value={summary.trustedExamples} helper="Exemplos promovidos para uso" tone="is-info" />
      </div>

      <div className="deadline-learning-metrics">
        <MetricCard label="Candidatos" value={summary.totalCandidates} helper="Frases candidatas à curadoria" tone="is-info" />
        <MetricCard label="Ocorrências" value={summary.totalMatches} helper="Total consolidado nos stats" tone="is-success" />
        <MetricCard label="Origem" value={summary.latestSource} helper="Última origem observada na base" tone="is-warning" />
        <LastImportMetric lastImportedAt={lastImportedAt} />
      </div>

      <Visible when={loading}><div className="deadline-learning-state">Carregando aprendizado...</div></Visible>

      <div className="deadline-learning-filters">
        <label><span>Confirmed Action Code</span><select value={actionFilter} onChange={(event) => { setActionFilter(event.target.value); setPage(1) }}><option value="">Todos</option>{actions.map((action) => <option key={getField(action, 'id', 'Id')} value={getField(action, 'code', 'Code')}>{getField(action, 'code', 'Code')}</option>)}</select></label>
        <label><span>Status</span><select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setPage(1) }}><option value="">Todos</option><option value="PENDING">Pendente</option><option value="CORRECT">Correto</option><option value="INCORRECT">Corrigido</option></select></label>
        <label><span>Processo ou publicação</span><input value={searchFilter} onChange={(event) => { setSearchFilter(event.target.value); setPage(1) }} placeholder="Buscar..." /></label>
      </div>

      <Visible when={hasNoItems(loading, examples)}>
        <div className="deadline-learning-state">
          Nenhum exemplo de aprendizado encontrado. Use a importação do histórico público para iniciar a base supervisionada.
        </div>
      </Visible>

      <Visible when={hasItems(loading, examples)}>
        <PanelSection
          title="Exemplos aprendidos"
          description="Casos históricos em analysis_learning_examples, aguardando confirmação humana do código e do trecho seletor."
        >
          <div className="deadline-learning-table-wrapper">
            <table className="users-table deadline-learning-table">
              <thead>
                <tr>
                  <th>Origem</th>
                  <th>Processo</th>
                  <th>Publicação</th>
                  <th>Interpretação humana</th>
                  <th>Ação final</th>
                  <th>Data fatal</th>
                  <th>Confiança</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {latestExamples.map((example) => <LearningExampleRow key={getField(example, 'id', 'Id')} example={example} onReview={openExampleReview} />)}
              </tbody>
            </table>
          </div>
          <div className="deadline-learning-pagination">
            <small>{totalExamples} registro(s) — página {page} de {Math.max(1, Math.ceil(totalExamples / pageSize))}</small>
            <div><button type="button" className="btn btn-sm btn-outline" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>Anterior</button><button type="button" className="btn btn-sm btn-outline" disabled={page * pageSize >= totalExamples || loading} onClick={() => setPage((value) => value + 1)}>Próxima</button></div>
          </div>
        </PanelSection>
      </Visible>

      <Visible when={hasItems(loading, topStats)}>
        <PanelSection
          title="Estatísticas do aprendizado"
          description="Resumo consolidado das frases aprendidas e da precisão estimada por ação."
        >
          <div className="deadline-learning-table-wrapper">
            <table className="users-table deadline-learning-table">
              <thead>
                <tr>
                  <th>Ação</th>
                  <th>Frase normalizada</th>
                  <th>Total</th>
                  <th>Confirmados</th>
                  <th>Rejeitados</th>
                  <th>Precisão</th>
                  <th>Confiança</th>
                </tr>
              </thead>
              <tbody>
                {topStats.map((stat) => {
                  const statId = getField(stat, 'id', 'Id')
                  return (
                    <tr key={statId}>
                      <td>{getField(stat, 'actionCode', 'ActionCode') || '-'}</td>
                      <td className="deadline-learning-phrase">{getField(stat, 'normalizedPhrase', 'NormalizedPhrase') || '-'}</td>
                      <td>{getField(stat, 'totalMatches', 'TotalMatches', 0)}</td>
                      <td>{getField(stat, 'confirmedMatches', 'ConfirmedMatches', 0)}</td>
                      <td>{getField(stat, 'rejectedMatches', 'RejectedMatches', 0)}</td>
                      <td>{formatPercent(getField(stat, 'precisionEstimate', 'PrecisionEstimate'))}</td>
                      <td>{formatPercent(getField(stat, 'confidence', 'Confidence'))}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </PanelSection>
      </Visible>

      <Visible when={hasItems(loading, candidates)}>
        <PanelSection
          title="Candidatos de curadoria"
          description="Padrões gerados pelo aprendizado importado e pelo feedback humano."
          action={
            <button type="button" className="btn btn-outline" onClick={loadLearningState} disabled={loading || importing}>
              <BusyIcon busy={loading} idleIcon="fa-rotate-right" />
              Recarregar candidatos
            </button>
          }
        >
          <div className="deadline-learning-table-wrapper">
            <table className="users-table deadline-learning-table">
              <thead>
                <tr>
                  <th>Ação</th>
                  <th>Frase</th>
                  <th>Tipo</th>
                  <th>Confiança</th>
                  <th>Fonte</th>
                  <th>Contexto</th>
                  <th>Status</th>
                  <th>Ocorrências</th>
                  <th>Usuários</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((candidate) => {
                  const candidateId = candidateIdOf(candidate)
                  const saving = savingId === candidateId
                  return (
                    <tr key={candidateId}>
                      <td>{getField(candidate, 'actionCode', 'ActionCode') || '-'}</td>
                      <td className="deadline-learning-phrase">{getField(candidate, 'phrase', 'Phrase') || '-'}</td>
                      <td>{getField(candidate, 'patternType', 'PatternType') || '-'}</td>
                      <td>{formatPercent(getField(candidate, 'confidence', 'Confidence'))}</td>
                      <td>{getField(candidate, 'source', 'Source') || '-'}</td>
                      <td>{[getField(candidate, 'contextArea', 'ContextArea'), getField(candidate, 'documentNature', 'DocumentNature')].filter(Boolean).join(' · ') || '-'}</td>
                      <td><CandidateBadge candidate={candidate} /></td>
                      <td>{getField(candidate, 'supportCount', 'SupportCount', 0)}</td>
                      <td>{getField(candidate, 'distinctUserCount', 'DistinctUserCount', 0)}</td>
                      <td>
                        <div className="deadline-learning-row-actions">
                          <button type="button" className="btn btn-sm btn-outline" onClick={() => openReviewModal(candidate)} disabled={saving}>
                            <i className="fas fa-pen" /> Revisar
                          </button>
                          <button type="button" className="btn btn-sm btn-primary" onClick={() => quickReview(candidate, true)} disabled={saving || getField(candidate, 'reviewed')}>
                            <i className="fas fa-check" /> Aprovar
                          </button>
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => quickReview(candidate, false)} disabled={saving || getField(candidate, 'reviewed')}>
                            <i className="fas fa-ban" /> Rejeitar
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </PanelSection>
      </Visible>

      <Visible when={hasNoItems(loading, candidates)}>
        <div className="deadline-learning-state">
          Nenhum candidato disponível no momento. A importação histórica ou os feedbacks do simulador podem alimentar essa lista.
        </div>
      </Visible>

      <div className="deadline-learning-footer">
        <small>Última atualização: {lastLoadedAt ? formatDateTime(lastLoadedAt) : 'Ainda não atualizada.'}</small>
      </div>

      <CandidateReviewModal
        open={reviewModalOpen}
        candidate={reviewCandidate}
        approved={reviewApproved}
        saving={savingId === candidateIdOf(reviewCandidate)}
        onClose={closeReviewModal}
        onChangeApproved={setReviewApproved}
        onSubmit={submitReview}
      />
      <ExampleReviewModal open={exampleModalOpen} example={reviewExample} actions={actions} saving={savingExample} onClose={closeExampleReview} onSubmit={validateExample} />
    </div>
  )
}
